import { Alert, AlertTitle, Card, Container, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useRef } from "react";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";
import PageHeader from "src/components/page-header";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import NoneFoundReport from "src/components/none-found-report";
import { TruxTableCell } from "src/components/trux-table-cell";
import Label from "src/components/label";
import { enqueueSnackbar } from "notistack";
import { DownloadExcelButton, ReportRunner, useDateRangeFilter } from "../runner/report-runner";

type UnregisteredFuelingLine = {
	truckName: string,
	driverName: string,
	date: string,
	time: string,
	location: string,
	fuelGallons: number,
	amountBilled: number,
	fuelBefore: number | null,
	fuelAfter: number | null,
	status: string,
	occurredAt: string,
}

function StatusLabel({ status }: { status: string }) {
  if (status === "Reconciled") {
    return <Label variant="filled" color="success">Reconciled</Label>;
  }
  if (status === "Not Reconciled") {
    return <Label variant="filled" color="error">Not Reconciled</Label>;
  }
  return <Label variant="filled" color="warning">Unverifiable</Label>;
}

type UnregisteredFuelingReportLinesProps = {
  lines: UnregisteredFuelingLine[],
}

const UnregisteredFuelingReportLines = memo(({ lines }: UnregisteredFuelingReportLinesProps) => {
  const { sortedList, sortData } = useSort(lines, "occurredAt", "desc");

  if (!sortedList) {
    return null;
  }

  return <div>
    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader sortData={sortData} sortKey="truckName">
                Truck
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="driverName">
                Driver
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="occurredAt">
                Date
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="occurredAt">
                Time
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="location">
                Location
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="fuelGallons">
                Gallons
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="amountBilled">
                Amount
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="status">
                Status
              </SortableHeader>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedList.map((line, index) =>
              (<TableRow key={index}>
                <TruxTableCell>
                  {line.truckName}
                </TruxTableCell>
                <TruxTableCell>
                  {line.driverName}
                </TruxTableCell>
                <TruxTableCell>
                  {line.date}
                </TruxTableCell>
                <TruxTableCell>
                  {line.time}
                </TruxTableCell>
                <TruxTableCell>
                  {line.location}
                </TruxTableCell>
                <TruxTableCell>
                  {line.fuelGallons.toFixed(2)}
                </TruxTableCell>
                <TruxTableCell>
                  ${line.amountBilled.toFixed(2)}
                </TruxTableCell>
                <TruxTableCell>
                  <StatusLabel status={line.status} />
                </TruxTableCell>
              </TableRow>
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div>;
});

type PageModel = {
  lines?: UnregisteredFuelingLine[],
  availability?: {
    hasIntegration: boolean,
  },
}

export default function Page() {
  const settings = useSettingsContext();

  const currentRequest = useRef({} as any);

  const dataLoader = useCallback(() => ({
    availability: axios.get(
      endpoints.reports.unregisteredFueling.availability
    ).then(response => response.data),
    lines: (currentRequest.current as any).ran && axios.post(
      endpoints.reports.unregisteredFueling.data,
      JSON.stringify(currentRequest.current)
    ).then(response => response.data.lines),
  }), []);
  const {
    data,
    error,
    loaded,
    loading,
    setShouldRefresh,
  } = useModelData<PageModel>(dataLoader);

  const dateRangeFilter = useDateRangeFilter();

  const filters = [
    dateRangeFilter
  ];

  const hasIntegration = data?.availability?.hasIntegration;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        <PageHeader title="Unregistered Fueling">
          {hasIntegration && <DownloadExcelButton baseUrl={endpoints.reports.unregisteredFueling.excel} filters={filters} currentRequest={currentRequest} />}
        </PageHeader>

        {loaded && data && !hasIntegration && (
          <Alert severity="info">
            <AlertTitle>Fuel card provider not connected</AlertTitle>
            This report needs your fuel card provider connected to your account. Please reach out
            to our customer support to get it connected: ask your fuel card provider for API access
            and CC support@countingtrucks.com and sales@countingtrucks.com directly in that email,
            or just talk to your sales representative.
          </Alert>
        )}

        {hasIntegration && (
          <ReportRunner
            filters={filters}
            onValidate={(errorMessage) => enqueueSnackbar(errorMessage, {variant: "error"})}
            onRun={(request) => {
              currentRequest.current = request;
              setShouldRefresh(true);
            }}
            loading={loading}
          />
        )}

        {hasIntegration && data && (data.lines?.length || 0) > 0 && <UnregisteredFuelingReportLines lines={data.lines || []} />}
        {hasIntegration && loaded && data && data.lines?.length === 0 && <NoneFoundReport />}
      </DisplayPageData>
    </Container>
  );
}
